import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  ArticleImage,
  DeliveryEstimate,
  ItemPrice,
  getArticlePriceData,
} from '@web/utils';
import { AddToCartButton } from '@/components/AddToCartButton';
import { ControlCartItem } from '@/components/ControlCartItem';
import { env } from '@/env';
import useCheckItemCartQuantity from '@/hooks/useCheckItemCartQuantity';
import { Article } from '@/types';

export interface InventoryCatalogItemProps {
  article: Article;
}

const ListArticle = (props: InventoryCatalogItemProps) => {
  const { article } = props;
  const { t } = useTranslation();

  const quantityInCart = useCheckItemCartQuantity(article) || 0;
  const { salePrice } = getArticlePriceData(article);

  return (
    <div
      className={clsx(
        'flex-row items-center border border-primary-border rounded-10-0 flex relative overflow-hidden',
      )}
    >
      {salePrice > 0 && (
        <div className="sale absolute right-0 top-0 bg-[#E62525] px-1-0 py-0-5">
          <p className="text-primary-white font-bd text-2-2 md:text-1-2 leading-1-76">
            {t('inventory.sale')}
          </p>
        </div>
      )}
      <div
        className={clsx('flex-row items-center gap-2-0 p-3-0 md:p-2-0 flex')}
      >
        <div className="w-full md:w-20-0">
          <p className="line-clamp-2 text-2-0 md:text-1-2 text-primary-black mb-2-0 md:mb-1-0">
            {article.taxonomy.name}
          </p>
          <div>
            <p className="max-w-[18rem] mb-0-5 text-2-2 md:text-1-4 font-bd">
              {article.name}
            </p>
            <p className="text-2-0 md:text-1-2">{article.subtitle}</p>
          </div>
          <div className="block md:hidden">
            <p className="text-2-0 md:text-1-2 min-h-[5rem] my-1-0">
              {article.description}
            </p>
            <div className="items-center flex">
              <DeliveryEstimate t={t} article={article} />
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center w-15-0 relative h-18-0 md:h-9-0 my-3-0 md:my-2-0">
          <ArticleImage
            article={article}
            env={env}
            className="mx-auto max-h-100-percent"
          />
        </div>
      </div>
      <div
        className={clsx(
          'hidden md:block border-primary-border px-3-0 md:px-2-0 py-1-0',
        )}
      >
        <p className="text-2-0 md:text-1-2 min-h-[5rem]">
          {article.description}
        </p>
        <div className={clsx('flex items-center mb-4-5 md:mb-2-5')}>
          <DeliveryEstimate t={t} article={article} />
        </div>
      </div>
      <div className="ml-auto p-3-0 md:p-2-0">
        <div className="flex-col gap-1-0 flex justify-between items-start min-h-[3rem]">
          <ItemPrice article={article} />
          {quantityInCart > 0 ? (
            <ControlCartItem
              article={article}
              quantityInCart={quantityInCart}
            />
          ) : (
            <AddToCartButton
              article={props.article}
              quantityInCart={quantityInCart}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ListArticle;
